import { ReportComponentType } from '@/types';
import { DatasetQuery, QueryResult } from '@/types/api/modules/business-intelligence';

// export const transformToBarData = (result: QueryResult, query: DatasetQuery) => {
//     if (!result?.rows?.length) return [];

//     const dimension = query.dimensions[0]?.field;
//     const metrics = query.metrics.map((m) => m.field);

//     return result.rows.map((row) => ({
//         [dimension]: row[dimension],
//         ...metrics.reduce((acc, m) => {
//             acc[m] = row[m];
//             return acc;
//         }, {} as any),
//     }));
// };

export const transformToBarData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return [];}

    const dimension = query.dimensions[0]?.field ?? 'category';
    const metrics = query.metrics.map((metric) => metric.field);

    return result.rows.map((row, index) => ({
        [dimension]: row[dimension] ?? row.company ?? row.symbol ?? `Result ${index + 1}`,
        ...metrics.reduce(
            (acc, metric) => {
                acc[metric] = row[metric] ?? 0;

                return acc;
            },
            {} as Record<string, unknown>,
        ),
    }));
};

export const transformToLineData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return [];}

    const dimension = query.dimensions?.[0]?.field; // e.g. date
    const metrics = query.metrics.map((m: any) => m.field);

    return metrics.map((metric: string) => ({
        id: metric,
        data: result.rows.map((row) => ({
            x: row[dimension],
            y: row[metric],
        })),
    }));
};

export const transformToPieData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return [];}

    const dimension = query.dimensions?.[0]?.field;
    const metric = query.metrics?.[0]?.field;

    return result.rows.map((row) => ({
        id: row[dimension],
        label: row[dimension],
        value: row[metric],
    }));
};

export const transformToHeatmapData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return [];}

    const [yDim, xDim] = query.dimensions.map((d: any) => d.field);
    const metric = query.metrics?.[0]?.field;

    const grouped: Record<string, any[]> = {};

    for (const row of result.rows) {
        if (!grouped[row[yDim]]) {grouped[row[yDim]] = [];}

        grouped[row[yDim]].push({
            x: row[xDim],
            y: row[metric],
        });
    }

    return Object.entries(grouped).map(([id, data]) => ({
        id,
        data,
    }));
};

export const transformToScatterData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return [];}

    const [xMetric, yMetric] = query.metrics.map((m: any) => m.field);
    const group = query.dimensions?.[0]?.field;

    if (!group) {
        return [
            {
                id: 'series',
                data: result.rows.map((row) => ({
                    x: row[xMetric],
                    y: row[yMetric],
                })),
            },
        ];
    }

    const grouped: Record<string, any[]> = {};

    for (const row of result.rows) {
        const key = row[group];
        if (!grouped[key]) {grouped[key] = [];}

        grouped[key].push({
            x: row[xMetric],
            y: row[yMetric],
        });
    }

    return Object.entries(grouped).map(([id, data]) => ({
        id,
        data,
    }));
};

export const transformToFunnelData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return [];}

    const dimension = query.dimensions?.[0]?.field;
    const metrics = query.metrics ?? [];

    if (metrics.length > 1 || !dimension) {
        const row = result.rows[0] ?? {};

        return metrics
            .map((metric) => {
                const value = Number(row[metric.field] ?? 0);

                if (!Number.isFinite(value)) {return undefined;}

                return {
                    id: metric.field,
                    label: metric.label ?? formatFieldLabel(metric.field),
                    value: Math.abs(value),
                };
            })
            .filter(Boolean);
    }

    const metric = metrics?.[0]?.field;

    return result.rows.map((row) => ({
        id: row[dimension],
        label: row[dimension],
        value: row[metric],
    }));
};

const formatFieldLabel = (field: string) =>
    field
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\bEbitda\b/i, 'EBITDA')
        .replace(/\bEbit\b/i, 'EBIT')
        .replace(/\bEps\b/i, 'EPS')
        .replace(/\b\w/g, (match) => match.toUpperCase());

export const transformToSankeyData = (result: QueryResult, query: DatasetQuery) => {
    const [sourceField, targetField] = query.dimensions.map((d: any) => d.field);
    const metric = query.metrics?.[0]?.field;

    const nodesSet = new Set<string>();
    const links: any[] = [];

    for (const row of result.rows) {
        nodesSet.add(row[sourceField]);
        nodesSet.add(row[targetField]);

        links.push({
            source: row[sourceField],
            target: row[targetField],
            value: row[metric],
        });
    }

    return {
        nodes: Array.from(nodesSet).map((id) => ({ id })),
        links,
    };
};

export const transformToRadarData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return [];}

    const dimension = query.dimensions?.[0]?.field;
    const metrics = query.metrics.map((m: any) => m.field);

    return result.rows.map((row) => ({
        axis: row[dimension],
        ...metrics.reduce(
            (acc, metric) => {
                acc[metric] = row[metric] ?? 0;

                return acc;
            },
            {} as Record<string, number>,
        ),
    }));
};

export const transformToRadialData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return [];}

    const [seriesDimension, categoryDimension] = query.dimensions.map((d: any) => d.field);
    const metric = query.metrics?.[0]?.field;

    if (!categoryDimension) {
        return [
            {
                id: query.metrics?.[0]?.label ?? metric ?? 'Value',
                data: result.rows.map((row) => ({
                    x: row[seriesDimension],
                    y: row[metric] ?? 0,
                })),
            },
        ];
    }

    const grouped: Record<string, any[]> = {};

    result.rows.forEach((row) => {
        const series = row[seriesDimension] ?? 'Series';

        if (!grouped[series]) {grouped[series] = [];}

        grouped[series].push({
            x: row[categoryDimension],
            y: row[metric] ?? 0,
        });
    });

    return Object.entries(grouped).map(([id, data]) => ({ id, data }));
};

export const transformToGaugeData = (result: QueryResult, query: DatasetQuery) => transformToPieData(result, query);

export const transformToChordData = (result: QueryResult, query: DatasetQuery) => {
    if (!result?.rows?.length) {return { keys: [], data: [] };}

    const [sourceField, targetField] = query.dimensions.map((d: any) => d.field);
    const metric = query.metrics?.[0]?.field;
    const keys = Array.from(
        new Set(
            result.rows
                .flatMap((row) => [row[sourceField], row[targetField]])
                .filter((value): value is string => typeof value === 'string'),
        ),
    );
    const keyIndex = new Map(keys.map((key, index) => [key, index]));
    const data = keys.map(() => keys.map(() => 0));

    result.rows.forEach((row) => {
        const sourceIndex = keyIndex.get(row[sourceField]);
        const targetIndex = keyIndex.get(row[targetField]);

        if (sourceIndex === undefined || targetIndex === undefined) {
            return;
        }

        data[sourceIndex][targetIndex] += Number(row[metric] ?? 0);
    });

    return { keys, data };
};

export const chartTransformers = {
    [ReportComponentType.BAR_CHART]: transformToBarData,
    [ReportComponentType.LINE_CHART]: transformToLineData,
    [ReportComponentType.AREA_CHART]: transformToLineData,
    [ReportComponentType.PIE_CHART]: transformToPieData,
    [ReportComponentType.DONUT_CHART]: transformToPieData,
    [ReportComponentType.HEAT_MAP]: transformToHeatmapData,
    [ReportComponentType.SCATTER_PLOT]: transformToScatterData,
    [ReportComponentType.FUNNEL_CHART]: transformToFunnelData,
    [ReportComponentType.SANKEY_CHART]: transformToSankeyData,
    [ReportComponentType.RADAR_CHART]: transformToRadarData,
    [ReportComponentType.RADIAL_CHART]: transformToRadialData,
    [ReportComponentType.GAUGE_CHART]: transformToGaugeData,
    [ReportComponentType.CHORD_CHART]: transformToChordData,
} as Record<ReportComponentType | any, (result: QueryResult, query: DatasetQuery, colorsArray?: string[]) => any>;
